import { Check, Copy } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { cn } from "@/lib/cn";
import { Button, IconButton } from "./button";
import { Tooltip } from "./tooltip";

export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      ta.remove();
      return ok;
    } catch {
      return false;
    }
  }
}

/**
 *   <CopyButton value={invoiceLink} />                 icon button with tooltip
 *   <CopyButton value={secret} label="Copy key" />      text button
 */
export function CopyButton({
  value,
  label,
  className,
  size = "sm",
  variant,
  toastMessage,
}: {
  value: string;
  label?: string;
  className?: string;
  size?: "xs" | "sm" | "md";
  variant?: "ghost" | "outline" | "secondary";
  toastMessage?: string;
}) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1600);
    return () => clearTimeout(t);
  }, [copied]);

  const onCopy = async () => {
    const ok = await copyToClipboard(value);
    if (ok) {
      setCopied(true);
      if (toastMessage) toast.success(toastMessage);
    } else {
      toast.error("Couldn't copy. Select the text and copy it manually.");
    }
  };

  const icon = copied ? <Check className="text-success" /> : <Copy />;
  if (label) {
    return (
      <Button variant={variant === "ghost" ? "ghost" : "secondary"} size={size === "md" ? "md" : size === "xs" ? "xs" : "sm"} onClick={onCopy} leftIcon={icon} className={className}>
        {copied ? "Copied" : label}
      </Button>
    );
  }
  return (
    <Tooltip content={copied ? "Copied" : "Copy"}>
      <IconButton aria-label={copied ? "Copied" : "Copy to clipboard"} size={size === "md" ? "md" : size === "xs" ? "xs" : "sm"} variant={variant === "outline" ? "outline" : "ghost"} onClick={onCopy} className={cn(className)}>
        {icon}
      </IconButton>
    </Tooltip>
  );
}
